import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, from, of, Observable } from 'rxjs';
import { concatMap, switchMap, map, catchError } from 'rxjs/operators';

import { ToastService } from '../../service/toast.service';
import { EntraService } from '../../service/entra.service';
import { SelectedLocation } from '../../models/selectedLocation.interface';
import { SofReceivingApiService } from './api/sof-receiving-api.service';
import { SofReceivingOrder } from '../../models/sof-receiving/SofReceivingOrder.interface';
import { SofOrderDetails } from '../../models/sof-receiving/SofOrderDetails.interface';
import { SofPoNumber } from '../../models/sof-receiving/SofPoNumber.interface';
import { SofItemEdit } from '../../models/sof-receiving/SofReceivingLogItem.interface';
import { SofReceivingLogUpdateResult } from '../../models/sof-receiving/SofReceivingLogUpdate.interface';
import { SofOperationResponse } from '../../models/sof-receiving/SofStatusUpdate.interface';
import { SofPhotoMetadata } from '../../models/sof-receiving/SofPhoto.interface';
import { LogEntryPhoto } from '../../models/logs/photos/LogEntryPhoto.interface';
import { convertPhotosToFiles } from '../../shared/helpers/photos-utils';
import {
  applyNotesPrefix,
  canComplete,
  dedupeOrdersBySof,
  itemsToPersist,
  sortOrdersSoonestFirst,
  toItemDrafts,
  toStorageFacility,
} from './rules';

/** Per-SOF aggregate meta shown on the queue cards. */
export interface SofQueueMeta {
  poCount: number;
  pulledCount: number;
  totalCount: number;
  locationCount: number;
  /** True when any PO has non-empty partialNotes ("Partial PO" pill). */
  hasPartial: boolean;
  /**
   * Every PO number on the SOF. Carried here purely so the queue's search box
   * can match on PO — PO numbers are not on the queue row itself, and this
   * fan-out has already fetched them.
   */
  poNumbers: string[];
}

/** Confirmation-panel data shown after a successful Mark Complete. */
export interface SofCompletionSummary {
  sof: string;
  /** The POs that were marked complete (the ones with pulled items). */
  completedPos: string[];
  orderTo: string | null;
  stagedAt: string;
  itemCount: number;
  stagedBy: string;
  /**
   * True when completing these POs rolled the whole SOF up to Complete (i.e.
   * every PO on the SOF is now complete). False means other POs still need
   * receiving before the SOF is fully done.
   */
  sofComplete: boolean;
}

/**
 * SofReceivingStateService holds the shared state for the SOF Receiving
 * screens (queue, detail, history) and orchestrates the API calls. Both the
 * mobile card flow and the desktop master-detail layout consume this same
 * service — only the templates differ.
 *
 * Items for ALL of a SOF's POs are loaded together so the picker can check
 * across POs on one screen. Save (In Progress) and Mark Complete both persist
 * one item batch (`itemsToPersist`: dirty rows plus every checked row, so the
 * server's `SOF #<sof>` notes tag lands on everything being staged), write the
 * SOF-level staging area once, then advance the status of each PO the picker
 * actually pulled from. The SOF rolls up to Complete automatically once its
 * last PO is complete.
 *
 * The facility is never picked inside SOF Receiving: the host app's selected
 * location (SelectedLocationService) is mapped through the explicit
 * storage-facility map and pushed in here by the page component via
 * setLocation(). An unmapped location leaves storageFacility null, which the
 * queue renders as a clear empty state.
 */
@Injectable({
  providedIn: 'root',
})
export class SofReceivingStateService {
  private _toastService: ToastService;
  private _api: SofReceivingApiService;
  private _entraService: EntraService;

  /** True on the history route — read-only variant, no editing/saving. */
  public readOnly = false;

  /** Host location label for the page header (e.g. 'Kent'). */
  public locationLabel = '';
  /** Mapped SOF storage facility, or null when the location has none. */
  public storageFacility: string | null = null;

  // Queue
  public orders = new BehaviorSubject<SofReceivingOrder[]>([]);
  public ordersLoading = new BehaviorSubject<boolean>(false);
  /** sof → aggregate meta (PO count, pulled/total, locations, has-partial). */
  public metaBySof = new BehaviorSubject<Record<string, SofQueueMeta>>({});

  // Selection / detail
  public selectedOrder = new BehaviorSubject<SofReceivingOrder | null>(null);
  public sofDetails = new BehaviorSubject<SofOrderDetails | null>(null);
  public pos = new BehaviorSubject<SofPoNumber[]>([]);
  public detailLoading = new BehaviorSubject<boolean>(false);

  /** Every PO's items, flattened, each stamped with its poNumber. */
  public items = new BehaviorSubject<SofItemEdit[]>([]);
  public itemsLoading = new BehaviorSubject<boolean>(false);

  /** Free-text staging area, pre-filled from ReceivingStagingArea on SOF load. */
  public stagingArea = '';

  // Proof photos
  /** Persisted photos for the open SOF (SAS-URL'd; loaded on selectSof). */
  public photos = new BehaviorSubject<SofPhotoMetadata[]>([]);
  /** Newly captured photos (local data-URLs) awaiting the next Save/Complete. */
  public newPhotos = new BehaviorSubject<LogEntryPhoto[]>([]);
  public photosLoading = new BehaviorSubject<boolean>(false);

  public saving = new BehaviorSubject<boolean>(false);
  /** Non-null after a successful Mark Complete — drives the confirmation panel. */
  public completionSummary = new BehaviorSubject<SofCompletionSummary | null>(null);

  constructor(
    toastService: ToastService,
    api: SofReceivingApiService,
    entraService: EntraService
  ) {
    this._toastService = toastService;
    this._api = api;
    this._entraService = entraService;
  }

  // ── Derived helpers (shared by both layouts) ─────────────────────────────

  /** Items belonging to a given PO. */
  public itemsForPo(poNumber: string): SofItemEdit[] {
    return this.items.value.filter((i) => i.poNumber === poNumber);
  }

  /** Count of checked-out items across all POs. */
  public get checkedCount(): number {
    return this.items.value.filter((i) => i.checkedOut).length;
  }

  /** Total item count across all POs. */
  public get totalCount(): number {
    return this.items.value.length;
  }

  /** The checked items across all POs (what will be staged). */
  public get checkedItems(): SofItemEdit[] {
    return this.items.value.filter((i) => i.checkedOut);
  }

  /** Distinct PO numbers that have at least one checked item. */
  private checkedPoNumbers(): string[] {
    return Array.from(
      new Set(this.items.value.filter((i) => i.checkedOut).map((i) => i.poNumber))
    );
  }

  // ── Location / queue ─────────────────────────────────────────────────────

  /**
   * Applies the host app's selected location. Called by the page component's
   * SelectedLocationService subscription — also on every nav location change,
   * which clears any open SOF (its data belongs to the old location) and
   * reloads the queue for the new one.
   */
  public setLocation(location: SelectedLocation): void {
    this.locationLabel = location.label;
    this.storageFacility = toStorageFacility(location.name);
    this.clearSelection();
    this.loadQueue();
  }

  /** Loads the queue (active orders, or history in read-only mode). */
  public loadQueue(): void {
    if (!this.storageFacility) {
      this.orders.next([]);
      this.metaBySof.next({});
      return;
    }

    this.ordersLoading.next(true);
    const request$ = this.readOnly
      ? this._api.getOrdersHistory(this.storageFacility)
      : this._api.getOrders(this.storageFacility);

    request$.subscribe({
      next: (orders) => {
        // The backing views are SELECT DISTINCT over a per-item join, so a SOF
        // can come back as multiple rows (differing only in item-level status).
        // Collapse to one card per SOF, then sort soonest-delivery-first so the
        // most urgent pick is on top. History gets the same ordering.
        const deduped = sortOrdersSoonestFirst(dedupeOrdersBySof(orders));
        this.orders.next(deduped);
        this.ordersLoading.next(false);
        this.loadQueueMeta(deduped);
      },
      error: (err) => {
        this.orders.next([]);
        this.ordersLoading.next(false);
        this._toastService.showWarningMessage(
          err?.message ?? 'Failed to load SOF orders'
        );
      },
    });
  }

  /**
   * Loads each queue SOF's aggregate meta (PO count, pulled/total items,
   * distinct locations, has-partial) by fanning out getPos + getItems per SOF.
   * Reuses the same endpoints the detail view uses, so the counts match exactly.
   * Best-effort and decorative: a failure just leaves that SOF without meta and
   * never toasts.
   *
   * NOTE: this is N getPos + one getItems per PO per queue load. Fine for the
   * modest per-facility queues here; if queues grow, replace with a single
   * aggregate endpoint (see DEFERRED.md).
   */
  private loadQueueMeta(orders: SofReceivingOrder[], merge = false): void {
    if (orders.length === 0) {
      if (!merge) {
        this.metaBySof.next({});
      }
      return;
    }

    const lookups = orders.map((order) =>
      this._api.getPos(order.sof).pipe(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        switchMap((pos) => {
          if (pos.length === 0) {
            return of({ sof: order.sof, meta: null as SofQueueMeta | null });
          }
          return forkJoin(
            pos.map((po) => this._api.getItems(order.sof, po.poNumber))
          ).pipe(
            map((itemGroups) => {
              const items = itemGroups.flat();
              const locations = new Set<string>();
              items.forEach((i) =>
                (i.location ?? '')
                  .split(',')
                  .map((l) => l.trim())
                  .filter((l) => l !== '')
                  .forEach((l) => locations.add(l))
              );
              const meta: SofQueueMeta = {
                poCount: pos.length,
                totalCount: items.length,
                pulledCount: items.filter((i) => i.checkedOut).length,
                locationCount: locations.size,
                hasPartial: pos.some((po) => !!po.partialNotes?.trim()),
                poNumbers: pos.map((po) => po.poNumber),
              };
              return { sof: order.sof, meta };
            })
          );
        }),
        catchError(() => of({ sof: order.sof, meta: null as SofQueueMeta | null }))
      )
    );

    forkJoin(lookups).subscribe({
      next: (results) => {
        // `merge` keeps the meta already loaded for every OTHER card. A full
        // queue load replaces the map outright; a single-SOF refresh after a
        // save must not blank out the rest of the queue's counts.
        const bySof: Record<string, SofQueueMeta> = merge
          ? { ...this.metaBySof.value }
          : {};
        results.forEach((r) => {
          if (r.meta) {
            bySof[r.sof] = r.meta;
          }
        });
        this.metaBySof.next(bySof);
      },
      error: () => {
        if (!merge) {
          this.metaBySof.next({});
        }
      },
    });
  }

  // ── SOF detail ───────────────────────────────────────────────────────────

  /**
   * Opens a SOF: loads its details, PO list, and the items for every PO at once
   * (so the picker can work across POs). Pre-fills the staging area.
   */
  public selectSof(order: SofReceivingOrder): void {
    this.selectedOrder.next(order);
    this.sofDetails.next(null);
    this.pos.next([]);
    this.items.next([]);
    this.stagingArea = '';
    this.completionSummary.next(null);
    this.photos.next([]);
    this.newPhotos.next([]);
    this.loadPhotos(order.sof);

    this.detailLoading.next(true);
    forkJoin({
      details: this._api.getSofDetails(order.sof),
      pos: this._api.getPos(order.sof),
    }).subscribe({
      next: ({ details, pos }) => {
        this.sofDetails.next(details);
        this.pos.next(pos);
        // Staging area is always uppercase on save; upper-case here too so a
        // value saved before that rule existed still displays consistently.
        this.stagingArea = (details?.receivingStagingArea ?? '').toUpperCase();
        this.detailLoading.next(false);
        this.loadAllItems(order.sof, pos);
      },
      error: (err) => {
        this.detailLoading.next(false);
        this._toastService.showWarningMessage(
          err?.message ?? 'Failed to load SOF details'
        );
      },
    });
  }

  /** Loads items for every PO and stamps the poNumber. Nothing is auto-checked
   *  — the picker always checks what they actually pulled. */
  private loadAllItems(sof: string, pos: SofPoNumber[]): void {
    if (pos.length === 0) {
      this.items.next([]);
      return;
    }

    this.itemsLoading.next(true);
    const lookups = pos.map((po) =>
      this._api.getItems(sof, po.poNumber).pipe(
        concatMap((items) => {
          // Capture the server's checked-out state per item — that's
          // "already pulled in a prior session" (locked, can't un-pull here).
          const rawPulled = items.map((i) => i.checkedOut === true);
          const drafts = toItemDrafts(items);
          const rows = drafts.map((draft, idx) => ({
            ...draft,
            poNumber: po.poNumber,
            alreadyPulled: rawPulled[idx],
          }));
          return of(rows);
        })
      )
    );

    forkJoin(lookups).subscribe({
      next: (groups) => {
        this.items.next(groups.flat());
        this.itemsLoading.next(false);
      },
      error: (err) => {
        this.items.next([]);
        this.itemsLoading.next(false);
        this._toastService.showWarningMessage(
          err?.message ?? 'Failed to load items'
        );
      },
    });
  }

  // ── Item editing ─────────────────────────────────────────────────────────

  /**
   * Toggles one item's checked-out state (tap semantics, 1:1). Already-pulled
   * items are locked — un-pulling them would mutate the shared MM.ReceivingLog
   * (a prior session's recorded work), so they can't be toggled here.
   */
  public toggleItem(id: number): void {
    if (this.readOnly) {
      return;
    }
    const target = this.items.value.find((item) => item.id === id);
    if (!target || target.alreadyPulled) {
      return;
    }
    this.items.next(
      this.items.value.map((item) =>
        item.id === id
          ? { ...item, checkedOut: !item.checkedOut, dirty: true }
          : item
      )
    );
  }

  /**
   * Select-all / none. Scoped to one PO when poNumber is given, otherwise all
   * items across every PO. Already-pulled (locked) items are left untouched.
   */
  public setAllItems(checkedOut: boolean, poNumber?: string): void {
    if (this.readOnly) {
      return;
    }
    this.items.next(
      this.items.value.map((item) => {
        if (poNumber && item.poNumber !== poNumber) {
          return item;
        }
        if (item.alreadyPulled) {
          return item;
        }
        return item.checkedOut === checkedOut
          ? item
          : { ...item, checkedOut, dirty: true };
      })
    );
  }

  /** Updates one item's notes (display-side as-is; the server adds the prefix). */
  public updateItemNotes(id: number, notes: string): void {
    if (this.readOnly) {
      return;
    }
    this.items.next(
      this.items.value.map((item) =>
        item.id === id ? { ...item, notes, dirty: true } : item
      )
    );
  }

  // ── Proof photos ─────────────────────────────────────────────────────────

  /** Loads the SOF's persisted photos (viewable while partial or complete). */
  public loadPhotos(sof: string): void {
    this.photosLoading.next(true);
    this._api.getPhotos(sof).subscribe({
      next: (response) => {
        this.photos.next(response?.photos ?? []);
        this.photosLoading.next(false);
      },
      error: () => {
        // Photos are supporting evidence, not the receiving data itself —
        // a failed load leaves the section empty without blocking the SOF.
        this.photos.next([]);
        this.photosLoading.next(false);
      },
    });
  }

  /** Adds freshly captured photos to the pending set (uploaded on save). */
  public addNewPhotos(photos: LogEntryPhoto[]): void {
    if (this.readOnly || photos.length === 0) {
      return;
    }
    this.newPhotos.next([...photos, ...this.newPhotos.value]);
  }

  /** Removes a not-yet-uploaded photo (local only). */
  public removeNewPhoto(index: number): void {
    const photos = [...this.newPhotos.value];
    photos.splice(index, 1);
    this.newPhotos.next(photos);
  }

  public clearNewPhotos(): void {
    this.newPhotos.next([]);
  }

  /**
   * Deletes a persisted photo. Allowed while the SOF's receiving is not yet
   * Complete (the server enforces the same rule with a 400).
   */
  public deletePhoto(index: number): void {
    const photo = this.photos.value[index];
    if (!photo || this.readOnly) {
      return;
    }
    this.photosLoading.next(true);
    this._api.deletePhoto(photo.id).subscribe({
      next: () => {
        this.photos.next(this.photos.value.filter((p) => p.id !== photo.id));
        this.photosLoading.next(false);
        this._toastService.showSuccessMessage('Photo deleted.');
      },
      error: (err) => {
        this.photosLoading.next(false);
        this._toastService.showWarningMessage(
          err?.message ?? 'Failed to delete photo'
        );
      },
    });
  }

  /** Deletes every persisted photo (used by the grid's Delete All). */
  public deleteAllPhotos(): void {
    const photos = this.photos.value;
    if (photos.length === 0 || this.readOnly) {
      return;
    }
    this.photosLoading.next(true);
    forkJoin(photos.map((photo) => this._api.deletePhoto(photo.id))).subscribe({
      next: () => {
        this.photos.next([]);
        this.photosLoading.next(false);
        this._toastService.showSuccessMessage('Photos deleted.');
      },
      error: (err) => {
        this.photosLoading.next(false);
        this._toastService.showWarningMessage(
          err?.message ?? 'Failed to delete photos'
        );
        // Some may have gone through — re-fetch so the list is authoritative.
        const sof = this.selectedOrder.value?.sof;
        if (sof) {
          this.loadPhotos(sof);
        }
      },
    });
  }

  /**
   * Uploads the pending photos as part of a Save/Complete. Photo failure is
   * non-fatal (matching Receiving Manager's posture on log submission): the
   * items/staging already saved, so we toast a warning and keep the pending
   * photos for the next attempt instead of failing the whole operation.
   */
  private uploadPendingPhotos$(sof: string): Observable<null> {
    const pending = this.newPhotos.value;
    if (pending.length === 0) {
      return of(null);
    }
    return from(convertPhotosToFiles(pending)).pipe(
      switchMap((files) => this._api.uploadPhotos(sof, files)),
      map(() => {
        this.newPhotos.next([]);
        this.loadPhotos(sof);
        return null;
      }),
      catchError((err) => {
        this._toastService.showWarningMessage(
          err?.message ?? 'Failed to upload photos — they were kept for retry.'
        );
        return of(null);
      })
    );
  }

  // ── Persist ──────────────────────────────────────────────────────────────

  /**
   * Save: persist the item batch → staging area → mark In Progress each PO
   * that has pulled items. With nothing pulled and nothing edited the batch is
   * empty and only the staging area is written. Leaves the SOF open.
   * Success/failure both toast.
   */
  public save(): void {
    this.persist(false);
  }

  /**
   * Mark Complete: requires a staging area AND at least one pulled item (client
   * checks; the server also enforces staging with a 400). Persists items +
   * staging, then completes each PO with pulled items, then shows the
   * confirmation panel.
   */
  public markComplete(): void {
    if (this.checkedCount === 0) {
      this._toastService.showWarningMessage(
        'Check the items you are staging before marking ready for delivery.'
      );
      return;
    }
    if (!canComplete(this.stagingArea)) {
      this._toastService.showWarningMessage(
        'Enter a staging area before marking ready for delivery.'
      );
      return;
    }
    this.persist(true);
  }

  private persist(complete: boolean): void {
    const order = this.selectedOrder.value;
    if (!order || this.readOnly) {
      return;
    }

    const sof = order.sof;
    const targetPos = this.checkedPoNumbers();
    // Dirty rows AND every currently checked row — the server tags the notes
    // with the SOF number, and a clean-but-checked row (notably one pulled in a
    // prior session) would otherwise never reach it. See itemsToPersist.
    const batch = itemsToPersist(this.items.value);
    const batchIds = new Set(batch.map((item) => item.id));

    const updateItems$: Observable<SofReceivingLogUpdateResult[] | null> =
      batch.length > 0
        ? this._api.updateItems({
            sof,
            items: batch.map((item) => ({
              id: item.id,
              checkedOut: item.checkedOut,
              notes: item.notes,
            })),
          })
        : of(null);

    this.saving.next(true);
    updateItems$
      .pipe(
        concatMap((results) => {
          const failed = (results ?? []).filter((r) => !r.success);
          if (failed.length > 0) {
            this._toastService.showWarningMessage(
              `${failed.length} item update${failed.length > 1 ? 's' : ''} failed`
            );
          }
          // Optimistic display of the server-side prefix, over the same rows we
          // actually sent (not just the dirty ones) so the UI matches storage.
          this.items.next(
            this.items.value.map((item) =>
              batchIds.has(item.id)
                ? { ...item, notes: applyNotesPrefix(sof, item.notes), dirty: false }
                : item
            )
          );
          // Every save writes the staging area. Save progress also appears on
          // the PICK step now, where the field is off screen — but `stagingArea`
          // is pre-filled from the SOF on load, so that write re-sends the value
          // already stored rather than blanking it.
          return this._api.updateStagingArea({
            sof,
            stagingArea: this.stagingArea,
          });
        }),
        // Proof photos ride along with both Save progress and Mark ready.
        // Uploaded before the status advances so a Complete can't outrun them.
        concatMap(() => this.uploadPendingPhotos$(sof)),
        concatMap(() => {
          // Advance the status of each PO the picker pulled from. No pulled
          // items on Save → nothing to advance (staging still saved).
          if (targetPos.length === 0) {
            return of([] as SofOperationResponse[]);
          }
          const calls = targetPos.map((po) =>
            complete
              ? this._api.setComplete({ sof, po })
              : this._api.setInProgress({ sof, po })
          );
          return forkJoin(calls);
        })
      )
      .subscribe({
        next: (responses: SofOperationResponse[]) => {
          this.saving.next(false);
          const failed = responses.filter((r) => !r.success);
          if (failed.length > 0) {
            this._toastService.showWarningMessage(
              failed[0].message ?? 'Status update failed'
            );
            return;
          }

          if (complete) {
            this.showCompletionSummary(sof, order.orderTo, targetPos);
          } else {
            const scope =
              targetPos.length > 0
                ? `${targetPos.length} PO${targetPos.length > 1 ? 's' : ''}`
                : 'staging';
            this._toastService.showSuccessMessage(`Progress saved (${scope}).`);
            this.refreshAfterSave(sof);
          }
        },
        error: (err) => {
          this.saving.next(false);
          this._toastService.showWarningMessage(
            err?.message ?? 'Failed to save receiving'
          );
        },
      });
  }

  /**
   * Builds the confirmation summary after a successful complete. Re-fetches the
   * SOF to report whether every PO is now done (SOF fully complete) or only the
   * pulled POs are.
   */
  private showCompletionSummary(
    sof: string,
    orderTo: string | null,
    completedPos: string[]
  ): void {
    const itemCount = this.checkedCount;
    const stagedBy = this._entraService.user.value.name;
    const stagedAt = this.stagingArea;

    this._api.getSofDetails(sof).subscribe({
      next: (details) => {
        const sofComplete =
          (details?.receivingStatus ?? '').trim().toLowerCase() === 'complete';
        this.completionSummary.next({
          sof,
          completedPos,
          orderTo,
          stagedAt,
          itemCount,
          stagedBy,
          sofComplete,
        });
      },
      error: () => {
        this.completionSummary.next({
          sof,
          completedPos,
          orderTo,
          stagedAt,
          itemCount,
          stagedBy,
          sofComplete: false,
        });
      },
    });

    this._toastService.showSuccessMessage(
      `Marked ready for delivery (${completedPos.length} PO${
        completedPos.length > 1 ? 's' : ''
      }).`
    );
  }

  /** Reloads items and SOF status after a save (server is authoritative). */
  private refreshAfterSave(sof: string): void {
    this._api.getSofDetails(sof).subscribe({
      next: (details) => {
        this.sofDetails.next(details);
        this.stagingArea = details?.receivingStagingArea
          ? details.receivingStagingArea.toUpperCase()
          : this.stagingArea;
        // The queue row carries its own copy of receivingStatus and is only
        // refetched on loadQueue, which a Save does not trigger. Without this
        // the card behind the open detail keeps its pre-save status — a SOF
        // moved to In Progress still read "Not started" on the queue until a
        // full reload. Patched from the same response the detail just used, so
        // the two cannot disagree.
        this.patchQueueStatus(sof, details?.receivingStatus ?? null);
      },
      error: () => {
        /* status refresh is best-effort; the save itself already succeeded */
      },
    });
    this.loadAllItems(sof, this.pos.value);
    this.refreshQueueMetaFor(sof);
  }

  /**
   * Rewrites one queue row's receivingStatus in place. A no-op when the SOF is
   * not in the current queue (History, or a filtered reload), and a no-op when
   * the status has not actually changed — re-emitting `orders` for nothing
   * would re-render every card and lose the rail's scroll position.
   */
  private patchQueueStatus(sof: string, receivingStatus: string | null): void {
    const orders = this.orders.value;
    const current = orders.find((order) => order.sof === sof);
    if (!current || current.receivingStatus === receivingStatus) {
      return;
    }
    this.orders.next(
      orders.map((order) =>
        order.sof === sof ? { ...order, receivingStatus } : order
      )
    );
  }

  /**
   * Recomputes one SOF's queue meta (pulled/total counts, has-partial) after a
   * save, so the card's "N/M pulled" strip matches what was just persisted.
   * Scoped to the one SOF rather than reloading every card's meta, which is a
   * getPos plus a getItems per PO for the whole queue.
   */
  private refreshQueueMetaFor(sof: string): void {
    if (!this.metaBySof.value[sof]) {
      return;
    }
    this.loadQueueMeta(
      this.orders.value.filter((order) => order.sof === sof),
      true
    );
  }

  /** Leaves the confirmation panel: clears the open SOF and reloads the queue. */
  public finishCompletion(): void {
    this.completionSummary.next(null);
    this.clearSelection();
    this.loadQueue();
  }

  /** Clears the open SOF/items (e.g. on nav location change). */
  public clearSelection(): void {
    this.selectedOrder.next(null);
    this.sofDetails.next(null);
    this.pos.next([]);
    this.items.next([]);
    this.stagingArea = '';
    this.completionSummary.next(null);
    this.photos.next([]);
    this.newPhotos.next([]);
  }
}
